/**
 * =============================================================================
 * IDS CRM Web - Parts used lines: quantities, costs and the line checks
 * =============================================================================
 *
 * Spare parts, Push 2 (build spec 9.4). Pure helpers shared by the job's Log
 * Work, the ticket's work log window, the Parts used list and Today:
 *
 *  - normaliseQuantityText tidies what a phone keyboard gives ('2,5' and '.5')
 *    before parseQuantity reads it. The server never accepts a comma.
 *  - parseQuantity and quantityText mirror api/src/utils/sparePricing.js:
 *    0.01 to 9999.99 with at most 2 decimal places, shown without trailing
 *    zeros ('2.50' is '2.5').
 *  - spareValueProblem reads a RAW saved Log Work value (asset_id, whole_job,
 *    name, quantity). Its sentences are the server's spareLineProblem word for
 *    word, so a phone stops on exactly what the server would refuse. (The
 *    server's twin takes camelCase lines, hence the different name.)
 *
 * No prices here: engineers never see a cost or a charge.
 * =============================================================================
 */

const QUANTITY_MIN_HUNDREDTHS = 1; // 0.01
const QUANTITY_MAX_HUNDREDTHS = 999999; // 9999.99

const isWholeHundredths = (n) => Math.abs(Math.round(n * 100) - n * 100) < 1e-6;

const hundredthsText = (h) => `${Math.floor(h / 100)}.${String(h % 100).padStart(2, '0')}`;

const lower = (value) => String(value ?? '').trim().toLowerCase();

/**
 * What was typed in a quantity box, tidied for parseQuantity: trimmed, a
 * comma read as a decimal point, and a leading point given its 0.
 *
 * @param {string|number|null} text
 * @returns {string} '2,5' is '2.5', '.5' is '0.5'
 */
export function normaliseQuantityText(text) {
  let tidy = String(text ?? '').trim().replace(/,/g, '.');
  if (tidy.startsWith('.')) tidy = `0${tidy}`;
  return tidy;
}

/**
 * A quantity: 0.01 to 9999.99 with at most 2 decimal places. A string must be
 * plain digits ('2', '2.5', '2.50'); tidy it with normaliseQuantityText first.
 *
 * @param {number|string} value
 * @returns {{ ok: true, text: string }|{ ok: false }} text is '2.50'-style
 */
export function parseQuantity(value) {
  let hundredths;
  if (typeof value === 'number') {
    if (!Number.isFinite(value) || !isWholeHundredths(value)) return { ok: false };
    hundredths = Math.round(value * 100);
  } else if (typeof value === 'string') {
    const match = /^(\d{1,4})(?:\.(\d{1,2}))?$/.exec(value);
    if (!match) return { ok: false };
    hundredths = Number(match[1]) * 100 + Number((match[2] || '').padEnd(2, '0'));
  } else {
    return { ok: false };
  }
  if (hundredths < QUANTITY_MIN_HUNDREDTHS || hundredths > QUANTITY_MAX_HUNDREDTHS) return { ok: false };
  return { ok: true, text: hundredthsText(hundredths) };
}

/**
 * A quantity for people to read: '2.50' is '2.5', '2.00' is '2', 2.5 is '2.5'.
 *
 * @param {number|string|null} q
 * @returns {string} '' when there is none
 */
export function quantityText(q) {
  if (q === null || q === undefined || q === '') return '';
  let text;
  if (typeof q === 'number') {
    if (!Number.isFinite(q)) return '';
    text = (Math.round(q * 100) / 100).toFixed(2);
  } else {
    text = String(q).trim();
  }
  if (!/^-?\d+\.\d*$/.test(text)) return text;
  return text.replace(/0+$/, '').replace(/\.$/, '');
}

/**
 * A purchase cost (pounds, up to 4 decimal places, as Postgres gives a
 * NUMERIC(12,4)) for the Settings list: '£0.6375', '£1,150.57', '£0.00'.
 * At least 2 decimal places, never trailing zeros beyond them.
 *
 * @param {string|number|null} cost
 * @returns {string} '—' when there is no cost
 */
export function formatPurchaseCost(cost) {
  if (cost === null || cost === undefined || cost === '') return '—';
  let text = typeof cost === 'number' ? (Number.isFinite(cost) ? cost.toFixed(4) : '') : String(cost).trim();
  const match = /^(\d+)(?:\.(\d+))?$/.exec(text);
  if (!match) return '—';
  text = match[1].replace(/^0+(?=\d)/, '');
  let fraction = (match[2] || '').slice(0, 4).replace(/0+$/, '');
  if (fraction.length < 2) fraction = fraction.padEnd(2, '0');
  const whole = text.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return `£${whole}.${fraction}`;
}

/**
 * Pounds as typed in a charge box ('12.50', '£1,150', '7') in whole pence,
 * or null when it is not an amount of at most 2 decimal places.
 *
 * @param {string} text
 * @returns {number|null}
 */
export function poundsToPence(text) {
  const tidy = String(text ?? '').trim().replace(/^£\s*/, '').replace(/,/g, '');
  const match = /^(\d{1,7})(?:\.(\d{1,2}))?$/.exec(tidy);
  if (!match) return null;
  return Number(match[1]) * 100 + Number((match[2] || '').padEnd(2, '0'));
}

/**
 * A cost per unit as typed ('£0.6375', '1,150.57'), tidied to plain text for
 * the API ('0.6375', '1150.57'): £0 to £99,999.9999 with at most 4 decimal
 * places. Null when it cannot be read.
 *
 * @param {string} text
 * @returns {string|null}
 */
export function costGbpText(text) {
  const tidy = String(text ?? '').trim().replace(/^£\s*/, '').replace(/,/g, '');
  return /^\d{1,5}(\.\d{1,4})?$/.test(tidy) ? tidy : null;
}

/** A cost in 1/10000 of a pound, for comparing '1.2' with '1.2000'; null when there is none. */
export function costTenThousandths(cost) {
  if (cost === null || cost === undefined || cost === '') return null;
  const match = /^(\d+)(?:\.(\d+))?$/.exec(String(cost).trim());
  if (!match) return null;
  return Number(match[1]) * 10000 + Number((match[2] || '').slice(0, 4).padEnd(4, '0'));
}

// ---------------------------------------------------------------------------
// The line checks (the server's spareLineProblem sentences, word for word)
// ---------------------------------------------------------------------------

export const SPARE_NO_NAME_MESSAGE = 'Parts used: a line has no part name. Remove it or choose the part again, then save again.';

export const spareQuantityMessage = (name) => `Parts used: the quantity for ${name} must be between 0.01 and 9999.99.`;

export const spareChooseRobotMessage = (name) => `Parts used: say which robot ${name} was fitted to, or choose Whole job, then save again.`;

export const spareRobotGoneMessage = (name) => `Parts used: the robot chosen for ${name} is no longer on this job. Choose another robot or Whole job, then save again.`;

/** Under a quantity box holding text that cannot be read. */
export const QUANTITY_HINT = 'Enter a quantity such as 1, 2 or 2.5 (up to 2 decimal places).';

/** The sentence that stops Save while a quantity box holds text that cannot be read. */
export const quantityBoxProblem = (name) => `Parts used: enter a quantity for ${name} such as 1, 2 or 2.5.`;

// The job's robot ids in lower case, from a Set or an array (the server
// compares them in lower case too).
const robotIdSet = (robotIds) => new Set([...(robotIds || [])].map(lower).filter(Boolean));

/**
 * Is this line's robot no longer on the job? (The line is flagged, and the
 * filing would refuse it.)
 */
export function spareRobotGone(value, robotIds) {
  if (!value || value.whole_job || !value.asset_id) return false;
  const robots = robotIdSet(robotIds);
  return !robots.has(lower(value.asset_id));
}

/**
 * What is wrong with one Parts used line, as the server would say it, or null.
 * Reads the RAW draft value: { name, quantity, asset_id, whole_job }.
 *
 * @param {object} value
 * @param {{ robotIds?: Set<string>, severalRobots?: boolean }} options
 *        robotIds holds the job's robot ids (compared in lower case); severalRobots
 *        defaults to robotIds.size > 1 (a ticket passes an empty Set)
 * @returns {string|null}
 */
export function spareValueProblem(value, { robotIds = new Set(), severalRobots } = {}) {
  const name = value && typeof value.name === 'string' ? value.name.trim() : '';
  if (!name) return SPARE_NO_NAME_MESSAGE;
  if (!parseQuantity(value.quantity).ok) return spareQuantityMessage(name);
  const robots = robotIdSet(robotIds);
  if (spareRobotGone(value, robots)) return spareRobotGoneMessage(name);
  const several = severalRobots === undefined ? robots.size > 1 : Boolean(severalRobots);
  if (!value.asset_id && value.whole_job !== true && several) return spareChooseRobotMessage(name);
  return null;
}

/** The first problem among several raw draft values, or null. */
export function firstSpareProblem(values, options = {}) {
  for (const value of values || []) {
    const problem = spareValueProblem(value, options);
    if (problem) return problem;
  }
  return null;
}

/**
 * A part as a summary names it: 'Custom Front Foot (PTL-11336A)', with its
 * Item Number when it has one, so two parts with the same name can be told
 * apart; just the name for a part not in the list.
 *
 * @param {string|null} name
 * @param {string|null} [itemNumber]
 * @returns {string}
 */
export function spareLineLabel(name, itemNumber) {
  const shown = String(name ?? '').trim() || 'A part';
  const number = String(itemNumber ?? '').trim();
  return number ? `${shown} (${number})` : shown;
}

/**
 * 'Brush Driver Shaft (PTL-11537A) ×2.5, Nozzle (NZ-200) ×1': the live Parts
 * used rows filed with one work log (GET /parts rows), for the line under a
 * work log card. Parts are filed on the crew's lead log, so pass the lead's
 * log id.
 *
 * @param {Array<object>} parts - GET /parts rows
 * @param {string} logId
 * @returns {string} '' when there are none
 */
export function partsSummaryForLog(parts, logId) {
  const id = lower(logId);
  if (!id) return '';
  return (Array.isArray(parts) ? parts : [])
    .filter(part => part && !part.voided_at && lower(part.work_log_id) === id)
    .map(part => `${spareLineLabel(part.part_name, part.part_number)} ×${quantityText(part.quantity_text ?? part.quantity)}`)
    .join(', ');
}
